import { setNotification } from "features/Notification/notificationSlice";
import { getCompany } from "features/company/companyActions";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Artwork Location — the folder the ERP Artwork Manager desktop app opens
 * for this company. A path on the shop's machines (a UNC share or a drive),
 * never on the web server: this page only stores it, the desktop app is what
 * opens it, and in a plain browser the Art Manager page's Open Folder button
 * stays disabled.
 *
 * Its own tile on the Settings page (section "Artwork Manager") and its own
 * endpoint, not a field on Company Info: that modal saves through the logo
 * upload as multipart, and this value has nothing to do with the company's
 * identity -- it is a machine setting. It was first placed as a card under
 * Company Address and nobody found it there.
 *
 * Reads the company from the redux slice and refreshes it on mount, so it
 * needs no props; a parent that already holds a fresh company row can pass
 * it as `companyInfo` (plus `refreshFunc`) and that wins.
 *
 * No path check here on purpose. A browser cannot tell whether a folder on
 * the shop server exists, and pretending to would only mislead. The desktop
 * app reports a bad path when it tries to open it.
 */
export default function ArtworkLocation({ companyInfo, refreshFunc }) {
  const dispatch = useDispatch();
  const sliceCompany = useSelector((state) => state.company?.company);
  const company = companyInfo || sliceCompany;
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  // Standalone use: take the row fresh rather than whatever was loaded at
  // login -- the setting may have been changed in another tab or in the app.
  useEffect(() => {
    if (!companyInfo) dispatch(getCompany());
  }, [companyInfo, dispatch]);

  useEffect(() => {
    setValue(company?.artworkLocation || "");
  }, [company?.artworkLocation]);

  const saved = company?.artworkLocation || "";
  const dirty = value.trim() !== saved;

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const { data } = await API.patch(
        "/companies/artwork-location",
        { artworkLocation: value.trim() },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Could not save");
      // Two readers to refresh: the settings page's own company fetch, which
      // feeds this card (and decides whether Save is enabled), and the
      // company slice, which everything else reads. CompInfo does the same.
      await refreshFunc?.();
      dispatch(getCompany());
      dispatch(
        setNotification({
          message: data.artworkLocation
            ? "Artwork Location saved"
            : "Artwork Location cleared",
          type: "success",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-4 pt-2 d-flex mb-2 bg-white shadow-sm"
      style={{ borderRadius: 10 }}
    >
      <div className="flex-grow-1">
        <div className="py-0 fw-bold fs-3 text-left">Artwork Manager</div>
        <hr className="my-0 mb-3" />
        <form onSubmit={save} className="row g-2 align-items-end">
          <div className="col-md-9">
            <label className="form-label mb-1 fw-bold" htmlFor="artworkLocation">
              Artwork Location
            </label>
            <input
              id="artworkLocation"
              className="form-control"
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="\\server\artwork  or  D:\Artwork"
              disabled={saving}
              spellCheck={false}
            />
            <div className="form-text">
              Folder on the shop server that the ERP Artwork Manager app opens.
              Any folder works for testing. Leave empty to clear it.
            </div>
          </div>
          <div className="col-md-3 d-flex gap-2">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={saving || !dirty}
              title={dirty ? undefined : "No change to save"}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {dirty && !saving && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setValue(saved)}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
